import { useEffect, useRef, useState } from 'react';
import { UI } from './i18n';
import { MANIFESTO_ZH } from './manifestoContent';
import { MANIFESTO_EN } from './manifestoContent.en';
import { MANIFESTO_ZH_TW } from './manifestoContent.zhTW';
import { MANIFESTO_JA } from './manifestoContent.ja';
import { MANIFESTO_KO } from './manifestoContent.ko';
import { MANIFESTO_VI } from './manifestoContent.vi';
import LangSwitcher from './LangSwitcher';

const logo = `${process.env.PUBLIC_URL}/logo.jpg`;
const CA = '0x234c3bFC260277cb4497E498A83aB685E30Ac730';
const SWAP_URL = `https://pancakeswap.finance/swap?outputCurrency=${CA}`;

// Convert markdown-ish **bold** to <strong>; keeps the rest as plain text.
function richText(s) {
  if (typeof s !== 'string') return s;
  const out = [];
  const re = /\*\*(.+?)\*\*/g;
  let last = 0;
  let m;
  let i = 0;
  while ((m = re.exec(s)) !== null) {
    if (m.index > last) out.push(s.slice(last, m.index));
    out.push(<strong key={i++}>{m[1]}</strong>);
    last = m.index + m[0].length;
  }
  if (last < s.length) out.push(s.slice(last));
  return out;
}

function Subsection({ s }) {
  return (
    <section id={s.id} className="ms-sub">
      <header className="ms-sub-head">
        <span className="ms-sub-num">{s.number}</span>
        <h3>{s.title}</h3>
      </header>
      {s.paragraphs?.map((p, i) => (
        <p key={`p-${i}`}>{richText(p)}</p>
      ))}
      {s.list && (
        <ol className="ms-list">
          {s.list.items.map((it, i) => (
            <li key={i}>{richText(it)}</li>
          ))}
        </ol>
      )}
      {s.paragraphsAfterList?.map((p, i) => (
        <p key={`pal-${i}`}>{richText(p)}</p>
      ))}
      {s.checklist && (
        <ul className="ms-check">
          {s.checklist.items.map((it, i) => (
            <li key={i}>
              <span className="ms-check-num">{i + 1}</span>
              <span>{richText(it)}</span>
            </li>
          ))}
        </ul>
      )}
      {s.pullQuote && <blockquote className="ms-quote">{s.pullQuote}</blockquote>}
      {s.paragraphsAfterQuote?.map((p, i) => (
        <p key={`paq-${i}`}>{richText(p)}</p>
      ))}
      {s.paragraphsTail?.map((p, i) => (
        <p key={`pt-${i}`}>{richText(p)}</p>
      ))}
    </section>
  );
}

function ChapterSection({ s }) {
  if (s.kind === 'pending') {
    return (
      <section id={s.id} className="ms-chapter ms-pending">
        <header className="ms-chapter-head">
          <span className="ms-chapter-num">{s.number}</span>
          <div>
            <h2>{s.title}</h2>
            <span className="ms-pending-tag">{s.note}</span>
          </div>
        </header>
      </section>
    );
  }

  return (
    <section id={s.id} className={`ms-chapter ms-${s.kind}`}>
      <header className="ms-chapter-head">
        {s.number && <span className="ms-chapter-num">{s.number}</span>}
        <div>
          <h2>{s.title}</h2>
        </div>
      </header>

      {s.pullQuote && s.kind === 'preface' && (
        <blockquote className="ms-quote ms-quote--lead">
          {s.pullQuote}
        </blockquote>
      )}

      {s.paragraphs?.map((p, i) => (
        <p key={`p-${i}`}>{richText(p)}</p>
      ))}

      {s.pullQuote && s.kind !== 'preface' && (
        <blockquote className="ms-quote">{s.pullQuote}</blockquote>
      )}

      {s.paragraphsAfterQuote?.map((p, i) => (
        <p key={`paq-${i}`}>{richText(p)}</p>
      ))}

      {s.subsections?.map((sub) => (
        <Subsection key={sub.id} s={sub} />
      ))}

      {s.paragraphsTail?.map((p, i) => (
        <p key={`pt-${i}`}>{richText(p)}</p>
      ))}

      {s.sign && <p className="ms-sign">{s.sign}</p>}
    </section>
  );
}

export default function Manifesto({ lang, setLang }) {
  const t = (obj) => (obj ? obj[lang] ?? obj.en ?? obj.zh : '');
  const content = (() => {
    if (lang === 'zhTW') return MANIFESTO_ZH_TW;
    if (lang === 'zh') return MANIFESTO_ZH;
    if (lang === 'ja') return MANIFESTO_JA;
    if (lang === 'ko') return MANIFESTO_KO;
    if (lang === 'vi') return MANIFESTO_VI;
    return MANIFESTO_EN;
  })();

  // Show compact header once the big title scrolls out of view.
  const titleRef = useRef(null);
  const [compactVisible, setCompactVisible] = useState(false);
  useEffect(() => {
    const target = titleRef.current;
    if (!target) return;
    const obs = new IntersectionObserver(
      ([entry]) => setCompactVisible(!entry.isIntersecting),
      { rootMargin: '-90px 0px 0px 0px', threshold: 0 }
    );
    obs.observe(target);
    return () => obs.disconnect();
  }, []);

  const onTocClick = (e, id) => {
    e.preventDefault();
    const el = document.getElementById(id);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      window.history?.replaceState(null, '', `#/manifesto/${id}`);
    }
  };

  const onHomeClick = (e) => {
    e.preventDefault();
    window.location.hash = '';
    window.scrollTo({ top: 0, behavior: 'auto' });
  };

  return (
    <div className={`App lang-${lang} manifesto-page`}>
      {/* ============ NAV ============ */}
      <nav className="nav">
        <div className="container nav-inner">
          <a href="#" className="nav-brand" onClick={onHomeClick}>
            <img src={logo} alt="ALLIN" />
            <span>ALLIN</span>
          </a>
          <div className="nav-links">
            <a href="#" onClick={onHomeClick}>
              {t(UI.manifesto.back)}
            </a>
          </div>
          <div className="nav-right">
            <LangSwitcher lang={lang} setLang={setLang} />
            <a
              href={SWAP_URL}
              target="_blank"
              rel="noreferrer"
              className="btn btn-dark"
            >
              {t(UI.nav.buy)}
            </a>
          </div>
        </div>
      </nav>

      {/* ============ COMPACT STICKY HEADER ============ */}
      <div
        className={`ms-compact ${compactVisible ? 'visible' : ''}`}
        aria-hidden={!compactVisible}
      >
        <div className="container ms-compact-inner">
          <span className="ms-compact-title">{t(UI.manifesto.title)}</span>
          <span className="ms-compact-sep" aria-hidden>·</span>
          <span className="ms-compact-sub">{t(UI.manifesto.subtitle)}</span>
        </div>
      </div>

      {/* ============ TITLE ============ */}
      <header className="ms-header">
        <div className="container">
          <span className="eyebrow">{t(UI.manifesto.eyebrow)}</span>
          <h1 ref={titleRef}>{t(UI.manifesto.title)}</h1>
          <p className="ms-subtitle">{t(UI.manifesto.subtitle)}</p>
          <div className="ms-edition">{t(UI.manifesto.edition)}</div>
        </div>
      </header>

      {/* ============ BODY: TOC + ARTICLE ============ */}
      <div className="ms-body">
        <div className="container">
          <div className="ms-grid">
            <aside className="ms-toc">
              <div className="ms-toc-inner">
                <span className="eyebrow">{t(UI.manifesto.tocTitle)}</span>
                <ol>
                  {content.sections.map((s) => (
                    <li
                      key={s.id}
                      className={s.kind === 'pending' ? 'pending' : ''}
                    >
                      <a
                        href={`#${s.id}`}
                        onClick={(e) => onTocClick(e, s.id)}
                      >
                        <span className="ms-toc-num">{s.number || '·'}</span>
                        <span className="ms-toc-title">{s.title}</span>
                      </a>
                      {s.subsections && (
                        <ol className="ms-toc-sub">
                          {s.subsections.map((sub) => (
                            <li key={sub.id}>
                              <a
                                href={`#${sub.id}`}
                                onClick={(e) => onTocClick(e, sub.id)}
                              >
                                {sub.number}. {sub.title}
                              </a>
                            </li>
                          ))}
                        </ol>
                      )}
                    </li>
                  ))}
                </ol>
              </div>
            </aside>

            <article className="ms-article">
              {content.sections.map((s) => (
                <ChapterSection key={s.id} s={s} />
              ))}

              <div className="ms-end">
                <p className="ms-end-note">{t(UI.manifesto.footerNote)}</p>
                <a
                  href="#"
                  onClick={onHomeClick}
                  className="btn btn-ghost"
                >
                  {t(UI.manifesto.back)}
                </a>
              </div>
            </article>
          </div>
        </div>
      </div>

      {/* ============ FOOTER ============ */}
      <footer className="footer">
        <div className="container">
          <div className="footer-bottom">
            <span>{t(UI.footer.copyright)}</span>
            <span>{t(UI.footer.disclaimer)}</span>
          </div>
        </div>
      </footer>
    </div>
  );
}
